import { Modal } from "react-bootstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";

const cancelButtonStyle = {
  padding: "8px 20px",
  borderRadius: "4px",
  border: "1px solid #1e40af",
  background: "#ffffff",
  color: "#1e40af",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
};

const saveButtonStyle = {
  padding: "8px 20px",
  borderRadius: "4px",
  border: "1px solid #0f172a",
  background: "#0f172a",
  color: "#ffffff",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  minWidth: 72,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const sectionHeaderStyle = {
  backgroundColor: "rgb(235, 240, 247)",
  color: "#003366",
  padding: "8px 12px",
  borderRadius: "4px",
  fontWeight: 600,
  marginBottom: "16px",
};

const REMARKS_MAX_LENGTH = 500;

const AddEditDepartmentModal = ({
  setDepartmentTableData,
  showAddEditModal,
  setShowAddEditModal,
  editDepartmentTableRow,
  setEditDepartmentTableRow,
  addDepartment,
  updateDepartment,
}) => {
  const isEditMode = Boolean(editDepartmentTableRow);

  const initialValues = {
    departmentId: editDepartmentTableRow?.departmentId ?? 0,
    departmentName: editDepartmentTableRow?.departmentName || "",
    isActive: editDepartmentTableRow
      ? editDepartmentTableRow.isActive === false ||
        editDepartmentTableRow.isActive === 0
        ? "false"
        : "true"
      : "true",
    remarks: editDepartmentTableRow?.remarks || "",
  };

  const validationSchema = Yup.object().shape({
    departmentName: Yup.string()
      .trim()
      .required("Please enter department name")
      .matches(/^[^\s]/, "Department name cannot start with a space")
      .min(3, "Department name must be at least 3 characters")
      .max(100, "Department name must not exceed 100 characters")
      .matches(
        /^[A-Za-z ]+$/,
        "Department name must contain only alphabets and spaces"
      ),
    isActive: Yup.string().required("Status is required"),
    remarks: Yup.string().max(
      REMARKS_MAX_LENGTH,
      "Remarks has exceeded the maximum allowed length."
    ),
  });

  const handleClose = () => {
    setShowAddEditModal(false);
    setEditDepartmentTableRow(null);
  };

  const onSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const payload = {
        departmentId: editDepartmentTableRow
          ? editDepartmentTableRow.departmentId
          : 0,
        departmentName: values.departmentName.trim(),
        isActive: values.isActive === "true" ? 1 : 0,
        remarks: values.remarks || "",
      };

      const result = editDepartmentTableRow
        ? await updateDepartment(payload)
        : await addDepartment(payload);

      if (result?.success) {
        toast.success(
          isEditMode
            ? "Department Updated Successfully"
            : "Department Created Successfully"
        );
        resetForm();
        handleClose();
        return;
      }

      const errorMessage =
        result?.message ||
        "Something went wrong!";
      const isDuplicate = /already exist/i.test(errorMessage);

      if (isDuplicate) {
        toast.error("Department already exists.");
      } else {
        toast.error(errorMessage);
      }
    } catch (error) {
      const responseData = error?.response?.data;
      const errorMessage =
        (typeof responseData === "string" ? responseData : null) ||
        responseData?.message ||
        responseData?.msg ||
        error?.message ||
        "Something went wrong!";
      const isDuplicate = /already exist/i.test(errorMessage);

      if (isDuplicate) {
        toast.error("Department already exists.");
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={showAddEditModal}
      onHide={handleClose}
      size="md"
      aria-labelledby="department-modal-title"
      centered
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title id="department-modal-title">
          {isEditMode ? "Edit Department" : "Add Department"}
        </Modal.Title>
        <i
          className="fa-solid fa-xmark close-container"
          onClick={handleClose}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              handleClose();
            }
          }}
          aria-label="Close modal"
        ></i>
      </Modal.Header>
      <hr />
      <Modal.Body>
        <Formik
          key={
            isEditMode
              ? editDepartmentTableRow?.departmentId ?? "edit"
              : "new"
          }
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {({ setFieldValue, isSubmitting }) => (
            <Form>
              <h6 className="mb-0" style={sectionHeaderStyle}>
                Department Information
              </h6>

              <div className="mb-3">
                <label htmlFor="departmentName" className="form-label">
                  Department Name <span className="text-danger">*</span>
                </label>
                <Field
                  type="text"
                  name="departmentName"
                  id="departmentName"
                  placeholder="Enter Department Name"
                  className="form-control"
                  autoComplete="off"
                  maxLength={100}
                  onChange={(event) => {
                    const sanitizedValue = event.target.value.replace(
                      /[^A-Za-z ]/g,
                      ""
                    );
                    setFieldValue("departmentName", sanitizedValue);
                  }}
                />
                <ErrorMessage
                  name="departmentName"
                  component="div"
                  className="small required-message"
                />
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Status <span className="text-danger">*</span>
                </label>
                <div className="d-flex align-items-center gap-4">
                  <div className="form-check mb-0">
                    <Field
                      type="radio"
                      className="form-check-input"
                      name="isActive"
                      id="departmentActive"
                      value="true"
                    />
                    <label
                      className="form-check-label"
                      htmlFor="departmentActive"
                    >
                      Active
                    </label>
                  </div>
                  <div className="form-check mb-0">
                    <Field
                      type="radio"
                      className="form-check-input"
                      name="isActive"
                      id="departmentInactive"
                      value="false"
                    />
                    <label
                      className="form-check-label"
                      htmlFor="departmentInactive"
                    >
                      Inactive
                    </label>
                  </div>
                </div>
                <ErrorMessage
                  name="isActive"
                  component="div"
                  className="small required-message"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="remarks" className="form-label">
                  Remarks
                </label>
                <Field
                  as="textarea"
                  name="remarks"
                  id="remarks"
                  placeholder="Enter Remarks"
                  className="form-control"
                  rows={3}
                  maxLength={REMARKS_MAX_LENGTH}
                  onChange={(event) => {
                    const value = event.target.value.slice(0, REMARKS_MAX_LENGTH);
                    setFieldValue("remarks", value);
                  }}
                />
                <ErrorMessage
                  name="remarks"
                  component="div"
                  className="small required-message"
                />
              </div>

              <hr />
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  style={cancelButtonStyle}
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={saveButtonStyle}
                  disabled={isSubmitting}
                >
                  {isEditMode ? "Update" : "Save"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default AddEditDepartmentModal;
